import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * CustomizeMiraPrompt - Session 2 free orb color picker
 *
 * Lets users pick a free Mira orb color to create investment/ownership.
 * Uses existing token shop items - no new backend required.
 *
 * Design: Simple, delightful, one action per screen
 */

// Track funnel actions for aggregated analytics (FERPA-compliant)
const trackFunnelAction = async (anonymousId, action) => {
  if (!anonymousId) return;
  try {
    await fetch('/api/guest/track-funnel-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        anonymous_id: anonymousId,
        action: action,
        session_type: 'mira_guest'
      })
    });
  } catch (error) {
    console.debug('Funnel tracking error:', error);
    // Silent fail - don't interrupt user flow
  }
};

const CustomizeMiraPrompt = ({
  isOpen,
  onClose,
  onSelectColor,
  currentColor,
  anonymousId  // Added for tracking
}) => {
  const [selectedColor, setSelectedColor] = useState(currentColor || null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  // Mira orb color options (matches token shop)
  const orbColors = [
    { id: 'coral', name: 'Coral', color: '#FF6B6B', gradient: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)' },
    { id: 'ocean', name: 'Ocean', color: '#4ECDC4', gradient: 'linear-gradient(135deg, #4ECDC4, #6EE7DF)' },
    { id: 'sunset', name: 'Sunset', color: '#FF9F43', gradient: 'linear-gradient(135deg, #FF9F43, #FFB76B)' },
    { id: 'midnight', name: 'Midnight', color: '#5F27CD', gradient: 'linear-gradient(135deg, #5F27CD, #7C4DFF)' },
    { id: 'forest', name: 'Forest', color: '#26DE81', gradient: 'linear-gradient(135deg, #26DE81, #4AE590)' },
    { id: 'lavender', name: 'Lavender', color: '#A55EEA', gradient: 'linear-gradient(135deg, #A55EEA, #C084FC)' }
  ];

  const handleConfirm = async () => {
    if (!selectedColor) return;

    setIsSubmitting(true);
    try {
      // Track the customization action
      trackFunnelAction(anonymousId, 'mira_customized');
      await onSelectColor(selectedColor);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.95 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          style={{
            backgroundColor: '#1c1c1e',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '380px',
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '28px 24px 20px',
            textAlign: 'center'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '22px',
              fontWeight: 600,
              color: '#ffffff',
              letterSpacing: '-0.3px'
            }}>
              Make Mira yours
            </h2>
            <p style={{
              margin: '10px 0 0',
              fontSize: '15px',
              color: '#8e8e93',
              lineHeight: 1.4
            }}>
              Pick a color for your AI tutor
            </p>
          </div>

          {/* Mira Preview */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '10px 0 20px'
          }}>
            <motion.div
              animate={{
                boxShadow: selectedColor
                  ? `0 0 60px ${orbColors.find(c => c.id === selectedColor)?.color}40`
                  : '0 0 60px rgba(41, 122, 146, 0.3)'
              }}
              transition={{ duration: 0.3 }}
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: selectedColor
                  ? orbColors.find(c => c.id === selectedColor)?.gradient
                  : 'linear-gradient(135deg, #297A92, #42909E)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}
            >
              {/* Mira eyes */}
              <div style={{
                display: 'flex',
                gap: '20px',
                marginTop: '-10px'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'white',
                  borderRadius: '50%'
                }} />
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'white',
                  borderRadius: '50%'
                }} />
              </div>
            </motion.div>
          </div>

          {/* Color Grid */}
          <div style={{
            padding: '0 24px 24px',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px'
          }}>
            {orbColors.map((orb) => (
              <motion.button
                key={orb.id}
                onClick={() => setSelectedColor(orb.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px 8px',
                  backgroundColor: selectedColor === orb.id
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(255, 255, 255, 0.03)',
                  border: selectedColor === orb.id
                    ? `2px solid ${orb.color}`
                    : '2px solid transparent',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: orb.gradient,
                  boxShadow: selectedColor === orb.id
                    ? `0 4px 20px ${orb.color}60`
                    : 'none'
                }} />
                <span style={{
                  fontSize: '13px',
                  color: selectedColor === orb.id ? '#ffffff' : '#8e8e93',
                  fontWeight: selectedColor === orb.id ? 500 : 400
                }}>
                  {orb.name}
                </span>
              </motion.button>
            ))}
          </div>

          {/* Free badge */}
          <div style={{
            padding: '0 24px 16px',
            textAlign: 'center'
          }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 14px',
              backgroundColor: 'rgba(48, 209, 88, 0.15)',
              color: '#30d158',
              borderRadius: '100px',
              fontSize: '13px',
              fontWeight: 500
            }}>
              Free for trying us out
            </span>
          </div>

          {/* Actions */}
          <div style={{
            padding: '8px 24px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <motion.button
              onClick={handleConfirm}
              disabled={!selectedColor || isSubmitting}
              whileHover={{ scale: selectedColor ? 1.02 : 1 }}
              whileTap={{ scale: selectedColor ? 0.98 : 1 }}
              style={{
                width: '100%',
                padding: '16px',
                background: selectedColor
                  ? orbColors.find(c => c.id === selectedColor)?.gradient
                  : 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: selectedColor ? 'pointer' : 'default',
                opacity: selectedColor ? 1 : 0.5,
                transition: 'all 0.2s ease'
              }}
            >
              {isSubmitting ? 'Saving...' : selectedColor ? 'Continue with this color' : 'Pick a color'}
            </motion.button>

            <button
              onClick={onClose}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'transparent',
                color: '#8e8e93',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Skip for now
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CustomizeMiraPrompt;
